import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableSelect from "@/components/SearchableSelect";
import api from "@/lib/api";
import { useSaveWithRetry } from "@/lib/saveRetry";
import { useDraftAutosave, DraftRestoredBanner } from "@/lib/draftAutosave";
import { formatIDR } from "@/lib/constants";

/**
 * CatatPengeluaranDialog — 149-cw-r129 R5.1 (2026-08-30).
 * Form terpusat untuk mencatat pengeluaran dengan:
 * - Proyek (aktif + internal Office/Head Office)
 * - Kategori (dropdown fixed)
 * - Bank/rekening sumber (master `/api/company-bank-accounts`)
 * - Reference number, Bukti (URL), Catatan
 * - Idempotency-key untuk cegah retry dobel
 * - Backend detect kemungkinan duplikat (tag `possible_duplicate_of`) untuk review manual.
 */
const OTHER_BANK = "__other__";
const CATEGORY_OPTIONS = [
  "Material", "Transport", "Konsumsi", "Utilitas", "Peralatan/Sewa",
  "Fee/Subcont", "Biaya Kantor", "Pajak/Retribusi", "Lain-lain",
];

function todayJKT() {
  const now = new Date();
  const jkt = new Date(now.getTime() + (7 * 60 - now.getTimezoneOffset()) * 60 * 1000);
  return jkt.toISOString().slice(0, 10);
}

export default function CatatPengeluaranDialog({ open, onOpenChange, projects = [], expenseRows = [], onChanged, editRow = null }) {
  // 2026-06-10 (user request): dukung mode edit — kalau `editRow` disediakan,
  // form di-prefill & tombol submit memanggil PATCH bukan POST.
  const isEdit = !!editRow;
  // 149-cw-r154: initialForm didefinisikan sekali di luar useState supaya
  // useDraftAutosave dapat compare "dirty" secara stabil (identity match).
  const initialForm = useMemo(() => ({
    date: todayJKT(), project_id: "", category: "", amount: "",
    bank_account_id: "", manual_bank_name: "", manual_account_no: "",
    reference_no: "", proof_url: "", note: "", wo_number: "",
  }), []);
  const [form, setForm] = useState(initialForm);
  const [banks, setBanks] = useState([]);
  // Sumber dana: bank (alur lama) | petty_cash (realisasi petty cash kanonis).
  const [fund, setFund] = useState("bank");
  const [holders, setHolders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [holderId, setHolderId] = useState("");
  const [pcId, setPcId] = useState("");
  useEffect(() => {
    if (open && fund === "petty_cash" && !holders.length) {
      api.get("/pc-manual-realizations/funding-options").then(({ data }) => setHolders(data.holders || [])).catch(() => setHolders([]));
    }
  }, [open, fund, holders.length]);
  useEffect(() => {
    setPcId(""); setSubs([]);
    if (holderId) {
      api.get("/pc-manual-realizations/funding-options", { params: { holder_id: holderId } }).then(({ data }) => setSubs(data.submissions || [])).catch(() => setSubs([]));
    }
  }, [holderId]);
  // 149-cw-r153: pakai useSaveWithRetry supaya kalau save gagal (network / access
  // cache stale), user dapat dialog "Coba Lagi" — data yg diketik tetap ada.
  const { runSave, RetryDialog, busy } = useSaveWithRetry();

  // 149-cw-r154: autosave draft ke localStorage tiap 5s + auto-restore saat dialog terbuka.
  const draft = useDraftAutosave({
    key: "catat-pengeluaran",
    form,
    setForm,
    enabled: open,
    initialForm,
  });

  useEffect(() => {
    if (open) {
      api.get("/company-bank-accounts").then(({ data }) => setBanks(Array.isArray(data) ? data : [])).catch(() => setBanks([]));
      // Mode edit: prefill dari row. Draft autosave dilewati (edit selalu spesifik).
      if (isEdit && editRow) {
        setForm({
          date: editRow.date || todayJKT(),
          project_id: editRow.__project_id || editRow.project_id || "",
          category: editRow.category || "",
          amount: String(Number(editRow.amount) || ""),
          bank_account_id: editRow.bank_account_id || (editRow.bank_name ? OTHER_BANK : ""),
          manual_bank_name: editRow.bank_name || "",
          manual_account_no: editRow.account_no || "",
          reference_no: editRow.reference_no || "",
          proof_url: editRow.proof_url || "",
          note: editRow.note || "",
          wo_number: editRow.wo_number || "",
        });
      }
    }
  }, [open, isEdit, editRow]);

  // 2026-06-10 (user request): kolom Proyek hanya tampilkan MAIN/INDUK project
  // (parent_project_id kosong) + operasional kantor. Anak WO/VO dipilih di
  // kolom "WO Nomor" di bawah, terfilter mengikuti induk yang dipilih.
  const projectOptions = useMemo(() => {
    return (projects || [])
      .filter((p) => !p.deleted_at)
      .filter((p) => p.project_type === "internal" || !p.parent_project_id)
      .sort((a, b) => {
        if (a.project_type === "internal" && b.project_type !== "internal") return -1;
        if (b.project_type === "internal" && a.project_type !== "internal") return 1;
        return (a.project_name || "").localeCompare(b.project_name || "");
      });
  }, [projects]);

  // 2026-06-10 (user request extended): dropdown "WO Nomor" berisi turunan project:
  //   - baris "Main" (induk itu sendiri — untuk pengeluaran tingkat project)
  //   - semua WO anak (project_type = working_order)
  //   - semua VO anak (project_type = variation_order / variation_order_name terisi)
  // Kolom ini tetap OPSIONAL. Nilai yang dikirim ke backend adalah wo_number
  // untuk WO, sedangkan Main/VO diberi label khusus (prefix "MAIN:"/"VO:") supaya
  // history & filter tetap dapat mengaitkan pengeluaran ke sub-project yang benar.
  const MANUAL_WO = "__manual_wo__";
  const MAIN_PREFIX = "MAIN:";
  const VO_PREFIX = "VO:";
  const [woManual, setWoManual] = useState(false);
  const woOptions = useMemo(() => {
    if (!form.project_id) return [];
    const sel = (projects || []).find((p) => p.id === form.project_id);
    if (!sel || sel.project_type === "internal") return [];
    const children = (projects || []).filter((p) => p.parent_project_id === sel.id && !p.deleted_at);
    const wos = children.filter((c) => c.project_type === "working_order" && c.wo_number)
      .map((w) => ({ value: w.wo_number, label: `WO ${w.wo_number}${w.wo_title ? ` · ${w.wo_title}` : ""}` }));
    const vos = children.filter((c) => c.variation_order_name || c.project_type === "variation_order")
      .map((v) => ({ value: `${VO_PREFIX}${v.variation_order_name || v.id}`, label: `VO · ${v.variation_order_name || "(tanpa nama)"}` }));
    // Baris Main pertama supaya user paham default = pengeluaran ke induk.
    return [
      { value: `${MAIN_PREFIX}${sel.id}`, label: `Main · ${sel.project_name || "-"}` },
      ...wos, ...vos,
    ];
  }, [form.project_id, projects]);

  // Draft restore: kalau nilai WO tersimpan bukan dari daftar → otomatis mode manual.
  useEffect(() => {
    if (open && form.wo_number) {
      const isKnown = form.wo_number.startsWith(MAIN_PREFIX)
        || form.wo_number.startsWith(VO_PREFIX)
        || woOptions.some((o) => o.value === form.wo_number);
      if (!isKnown) setWoManual(true);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const isOther = form.bank_account_id === OTHER_BANK;
  const selectedBank = banks.find((b) => b.id === form.bank_account_id) || null;
  const amountNum = parseFloat(String(form.amount).replace(/[^0-9.]/g, "")) || 0;

  // Client-side duplicate hint (informational only — backend also flags).
  const possibleDup = useMemo(() => {
    if (!form.date || amountNum <= 0 || !form.project_id) return null;
    const proj = projectOptions.find((p) => p.id === form.project_id);
    const projExp = (proj?.expense_entries || []);
    return projExp.find((e) => {
      // Mode edit: jangan cocokkan entri dengan DIRINYA SENDIRI.
      if (isEdit && editRow && e.id === editRow.id) return false;
      if (Number(e.amount) !== amountNum) return false;
      if (form.reference_no && (e.reference_no || "") === form.reference_no.trim()) return true;
      if (e.date === form.date && (e.note || "") === form.note.trim()) return true;
      return false;
    }) || null;
  }, [form, amountNum, projectOptions, isEdit, editRow]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_id) { toast.error("Pilih proyek/operasional kantor"); return; }
    if (!form.date) { toast.error("Tanggal wajib diisi"); return; }
    if (amountNum <= 0) { toast.error("Nominal wajib > 0"); return; }
    if (!form.category) { toast.error("Kategori pengeluaran wajib dipilih"); return; }
    if (!isEdit && fund === "petty_cash") {
      if (!form.note.trim()) { toast.error("Uraian (Catatan) wajib diisi"); return; }
      if (!holderId) { toast.error("Pilih pemegang petty cash"); return; }
      runSave(async () => {
        const { data } = await api.post("/pc-manual-realizations", {
          project_id: form.project_id, wo_number: form.wo_number.trim() || null,
          transaction_date: form.date, category: form.category, description: form.note.trim(),
          amount: amountNum, holder_id: holderId, pc_id: pcId || null,
          reference_no: form.reference_no.trim() || null, proof_url: form.proof_url.trim() || null,
          idempotency_key: `pcm-${form.project_id}-${form.date}-${amountNum}-${holderId}-${(form.reference_no || form.note).slice(0, 20)}`,
        });
        toast.success(data.status === "pending_fat" ? "Tersimpan · Menunggu Verifikasi FAT" : "Tersimpan · Sumber Belum Ditautkan");
        if (data.possible_duplicates?.length) toast.warning(`Kemiripan dengan ${data.possible_duplicates.length} transaksi lain — diperiksa FAT`);
        draft.clear(); setForm(initialForm); setHolderId(""); setFund("bank");
        onChanged?.(); onOpenChange(false);
      });
      return;
    }
    let bank_name = null, account_no = null, bank_account_id = null;
    if (form.bank_account_id) {
      if (isOther) {
        bank_name = form.manual_bank_name.trim() || null;
        account_no = form.manual_account_no.trim() || null;
        if (!bank_name || !account_no) { toast.error("Untuk 'Lainnya' isi Nama Bank & Nomor Rekening manual"); return; }
      } else if (selectedBank) {
        bank_name = selectedBank.bank_name; account_no = selectedBank.account_no; bank_account_id = selectedBank.id;
      }
    }
    const idem = `exp-${form.project_id}-${form.date}-${amountNum}-${(form.reference_no || form.note).slice(0, 20)}`;
    // 149-cw-r153: runSave — kalau throw, dialog retry akan tampil dgn tombol
    // Coba Lagi. Form state tidak di-reset supaya data yang diketik tidak hilang.
    runSave(async () => {
      if (isEdit) {
        // 2026-06-21 (user request): endpoint edit kini menerima SEMUA field form
        // termasuk rekening sumber — null eksplisit mengosongkan field di backend.
        await api.patch(`/active-projects/${editRow.__project_id || editRow.project_id || form.project_id}/expense/${editRow.id}`, {
          date: form.date,
          amount: amountNum,
          note: form.note.trim(),
          category: form.category || null,
          bank_account_id: isOther ? null : (bank_account_id || null),
          bank_name: bank_name || null,
          account_no: account_no || null,
          reference_no: form.reference_no.trim() || null,
          proof_url: form.proof_url.trim() || null,
          wo_number: form.wo_number.trim() || null,
        });
        toast.success("Pengeluaran diperbarui");
        draft.clear();
        onChanged?.();
        onOpenChange(false);
        return;
      }
      await api.post(`/active-projects/${form.project_id}/expense`, {
        date: form.date,
        amount: amountNum,
        note: form.note.trim() || undefined,
        category: form.category,
        bank_account_id,
        bank_name,
        account_no,
        reference_no: form.reference_no.trim() || undefined,
        proof_url: form.proof_url.trim() || undefined,
        wo_number: form.wo_number.trim() || undefined,
        finance_source: "laporan_keuangan",
        idempotency_key: idem,
      });
      toast.success("Pengeluaran tersimpan");
      // 149-cw-r154: clear draft setelah save sukses supaya tidak ke-restore lagi
      // saat dialog dibuka ulang.
      draft.clear();
      setForm(initialForm);
      onChanged?.();
      onOpenChange(false);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="catat-pengeluaran-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold uppercase tracking-wider text-[#0B1D3A]">{isEdit ? "Edit Pengeluaran" : "Catat Pengeluaran"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {/* 149-cw-r154: banner info kalau draft dipulihkan. */}
          <DraftRestoredBanner hasRestored={draft.hasRestored} restoredAt={draft.restoredAt} onDismiss={draft.dismiss} testid="catat-pengeluaran-draft-banner" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Tanggal Transaksi *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="exp-date" className="h-9 rounded-none" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Proyek / Operasional Kantor *</Label>
              {/* 2026-06-21: mode edit — proyek terkunci (memindah entri antar
                  proyek tidak didukung backend; edit hanya mengubah isi entri). */}
              {isEdit ? (
                <div className="min-h-9 px-2 py-1.5 flex items-center border border-slate-200 bg-slate-50 text-xs text-slate-600" data-testid="exp-project-readonly">
                  <span className="truncate">{projectOptions.find((p) => p.id === form.project_id)?.project_name || editRow?.project_name || "-"}</span>
                  <span className="ml-2 text-[9px] text-slate-400 italic shrink-0">tidak dapat dipindah saat edit</span>
                </div>
              ) : (
              <SearchableSelect
                testid="exp-project"
                value={form.project_id}
                onChange={(v) => setForm({ ...form, project_id: v, wo_number: "" })}
                options={projectOptions.map((p) => ({ value: p.id,
                  label: `${p.project_type === "internal" ? "🏢 " : ""}${p.project_name || p.wo_number || "-"}${p.client ? ` · ${p.client}` : ""}` }))}
                placeholder="Pilih proyek…"
                searchPlaceholder="Ketik nama proyek / client…"
                emptyText="Proyek tidak ditemukan"
              />
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Kategori *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="exp-category" className="rounded-none h-9"><SelectValue placeholder="Pilih kategori…" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {CATEGORY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">WO Nomor (opsional)</Label>
              {/* 2026-06-10: pilihan WO dari data existing, terfilter per project.
                  Bisa diketik untuk cari; "Lainnya" membuka input manual. */}
              <SearchableSelect
                testid="exp-wo"
                value={woManual ? MANUAL_WO : form.wo_number}
                onChange={(v) => {
                  if (v === MANUAL_WO) { setWoManual(true); setForm({ ...form, wo_number: "" }); }
                  else { setWoManual(false); setForm({ ...form, wo_number: v }); }
                }}
                placeholder={form.project_id ? "Pilih WO project ini…" : "Pilih WO…"}
                searchPlaceholder="Ketik nomor WO / nama project…"
                emptyText={form.project_id ? "Tidak ada WO untuk project ini" : "Tidak ada WO yang cocok"}
                options={[...woOptions, { value: MANUAL_WO, label: "Lainnya (ketik manual)" }]}
              />
              {woManual && (
                <Input
                  value={form.wo_number}
                  onChange={(e) => setForm({ ...form, wo_number: e.target.value })}
                  placeholder="e.g., WO-2026-0001"
                  data-testid="exp-wo-manual"
                  className="h-9 rounded-none mt-1.5"
                />
              )}
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="overline text-slate-600">Nominal *</Label>
              <Input type="text" inputMode="numeric" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" data-testid="exp-amount" className="h-9 rounded-none tabular-nums text-right pr-3" />
              {amountNum > 0 && <div className="text-[10px] text-slate-500 tabular-nums text-right">{formatIDR(amountNum)}</div>}
            </div>
            {!isEdit && (
              <div className="space-y-1.5 md:col-span-2">
                <Label className="overline text-slate-600">Sumber Dana *</Label>
                <div className="inline-flex border border-slate-300" data-testid="exp-fund-toggle">
                  {[{ v: "bank", l: "Rekening Bank" }, { v: "petty_cash", l: "Petty Cash" }].map((o) => (
                    <button key={o.v} type="button" onClick={() => setFund(o.v)} data-testid={`exp-fund-${o.v}`}
                      className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold ${fund === o.v ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{o.l}</button>
                  ))}
                </div>
              </div>
            )}
            {!isEdit && fund === "petty_cash" ? (
              <>
                <div className="space-y-1.5">
                  <Label className="overline text-slate-600">Pemegang Petty Cash *</Label>
                  <SearchableSelect testid="exp-pc-holder" value={holderId} onChange={setHolderId}
                    options={holders.map((h) => ({ value: h.id, label: h.name }))}
                    placeholder="Pilih pemegang…" searchPlaceholder="Ketik nama…" emptyText="Belum ada pemegang dengan pengajuan ditransfer" />
                </div>
                <div className="space-y-1.5">
                  <Label className="overline text-slate-600">Pengajuan Sumber Saldo *</Label>
                  <SearchableSelect testid="exp-pc-submission" value={pcId} onChange={setPcId}
                    options={subs.map((s) => ({ value: s.id, label: `${s.week_label || "-"} · ${s.project_name || "-"} · transfer ${formatIDR(s.transfer_amount ?? s.total ?? 0)}` }))}
                    placeholder={holderId ? "Pilih pengajuan yang sudah ditransfer…" : "Pilih pemegang dulu"} searchPlaceholder="Ketik minggu / proyek…" emptyText="Tidak ada pengajuan ditransfer" />
                </div>
                <div className="md:col-span-2 text-[10px] text-slate-500 border-l-2 border-amber-400 pl-2" data-testid="exp-pc-info">
                  Disimpan sebagai realisasi petty cash <b>Menunggu Verifikasi FAT</b>. Belum masuk biaya proyek, saldo resmi, maupun Cost Control sampai diverifikasi.
                  {holderId && !pcId && <span className="text-amber-700"> Tanpa pengajuan sumber, status menjadi <b>Sumber Belum Ditautkan</b> dan tidak bisa diverifikasi.</span>}
                </div>
              </>
            ) : (
            <>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="overline text-slate-600">Rekening Sumber (opsional)</Label>
              <SearchableSelect
                testid="exp-bank"
                value={form.bank_account_id}
                onChange={(v) => setForm({ ...form, bank_account_id: v })}
                options={[...banks.map((b) => ({ value: b.id, label: `${b.bank_name} · ${b.account_no}` })),
                  { value: OTHER_BANK, label: "Lainnya (isi manual)" }]}
                placeholder="Pilih rekening kantor…"
                searchPlaceholder="Ketik nama bank / no rekening…"
                emptyText="Rekening tidak ditemukan"
              />
            </div>
            {isOther && (
              <>
                <div className="space-y-1.5">
                  <Label className="overline text-slate-600">Nama Bank (manual)</Label>
                  <Input value={form.manual_bank_name} onChange={(e) => setForm({ ...form, manual_bank_name: e.target.value })} data-testid="exp-manual-bank" className="h-9 rounded-none" />
                </div>
                <div className="space-y-1.5">
                  <Label className="overline text-slate-600">Nomor Rekening (manual)</Label>
                  <Input value={form.manual_account_no} onChange={(e) => setForm({ ...form, manual_account_no: e.target.value })} data-testid="exp-manual-account" className="h-9 rounded-none" />
                </div>
              </>
            )}
            </>
            )}
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Reference Number (opsional)</Label>
              <Input value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} placeholder="e.g., TF-20260830-001" data-testid="exp-ref" className="h-9 rounded-none font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="overline text-slate-600">Bukti Pembayaran (URL opsional)</Label>
              <Input value={form.proof_url} onChange={(e) => setForm({ ...form, proof_url: e.target.value })} placeholder="Link Drive/foto…" data-testid="exp-proof" className="h-9 rounded-none" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="overline text-slate-600">{fund === "petty_cash" && !isEdit ? "Uraian *" : "Catatan"}</Label>
              <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Keterangan pengeluaran"
                data-testid="exp-note" className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:outline-none focus:border-[#0B1D3A] resize-none bg-white" />
            </div>
          </div>

          {possibleDup && (
            <div className="border border-amber-300 bg-amber-50 text-amber-800 text-[11px] px-3 py-2 flex items-start gap-2" data-testid="exp-dup-warn">
              <AlertTriangle size={13} className="shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Kemungkinan duplikat terdeteksi:</div>
                <div>{possibleDup.date} · {formatIDR(possibleDup.amount)} · {possibleDup.note || "(tanpa catatan)"}. Backend akan menyimpan tapi menandai untuk review manual.</div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={() => onOpenChange(false)} data-testid="exp-cancel"
              className="h-9 px-3 border border-slate-300 text-xs uppercase tracking-wider font-semibold text-slate-700 hover:bg-slate-50">
              Batal
            </button>
            <button type="submit" disabled={busy} data-testid="exp-submit"
              className="h-9 px-4 bg-[#0B1D3A] hover:bg-[#122a4d] text-white text-xs uppercase tracking-wider font-semibold disabled:opacity-50">
              {busy ? "Menyimpan…" : "Simpan"}
            </button>
          </div>
        </form>
        <RetryDialog />
      </DialogContent>
    </Dialog>
  );
}
